import OutputMultiLineGraph from '../Visualisations/outputs/OutputMultiLineGraph'
import { daysSince, formatDayAge } from '../../utils/activityAge'

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// Shared bar-chart visualisation for "Housevisits by status". Used by both
// `DashboardHouseVisitsStatusCard` (dashboard) and the upcoming
// `HouseVisitsStatusWidget` (live-report). The caller shapes the data and
// passes it in; the visualisation only renders.
//
// Interaction is delegated via the `onItemClick` slot: the dashboard wires
// it to deep-link into the housevisits overview filtered by status; the
// widget passes nothing → bars are inert (published reports are static).
//
// `showWaitingDays` toggles the "Xd" pill above the planning bar, which the
// dashboard uses to surface the oldest unscheduled visit. Defaults on so the
// widget gets parity automatically.

/**
 * @typedef {object} HouseVisitsStatusDatum
 * @property {string} name - Visible bar label (e.g. 'Inplannen')
 * @property {string} id - Status key (e.g. 'planning')
 * @property {string} statusId - Status key forwarded to onItemClick
 * @property {{ toDate: () => Date }|null} oldestTs - Firestore timestamp of the oldest item in this bucket, or null
 * @property {{ total: number }} value - Bar value
 */

/**
 * @param {object} props
 * @param {Array<HouseVisitsStatusDatum>} props.data
 * @param {string} props.color
 * @param {string|number} [props.height='100%']
 * @param {(item: HouseVisitsStatusDatum) => void} [props.onItemClick]
 * @param {boolean} [props.showWaitingDays=true]
 * @returns {JSX.Element}
 */
const HouseVisitsStatusVisualization = ({
    data,
    color,
    height = '100%',
    onItemClick,
    showWaitingDays = true,
}) => {
    const bars = [{ Title: 'value', Color: color }]

    const barTopLabel = showWaitingDays
        ? (payload, { x, y, width }) => {
            if (!payload || payload.id !== 'planning') return null
            const days = payload.oldestTs ? daysSince(payload.oldestTs) : null
            if (days === null) return null
            const label = formatDayAge(days)
            const pillH = 14
            const pillW = Math.max(22, label.length * 6 + 10)
            const cx = x + (width / 2)
            const py = (y || 0) - pillH - 4
            return (
                <g>
                    <rect
                        x={cx - pillW / 2}
                        y={py}
                        width={pillW}
                        height={pillH}
                        rx={pillH / 2}
                        ry={pillH / 2}
                        fill={color}
                    />
                    <text
                        x={cx}
                        y={py + pillH / 2}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fill="#fff"
                        className="housevisits-status-pill-text"
                    >
                        {label}
                    </text>
                </g>
            )
        }
        : undefined

    return (
        <OutputMultiLineGraph
            data={data}
            tableCategoryLabel="Status"
            tableValueLabel="Aantal"
            graphType="bar"
            outputs={bars}
            color={color}
            growth={{ average: 0, perOutput: {} }}
            height={height}
            displayLegend={false}
            onBarClick={onItemClick ? (payload) => onItemClick(payload) : undefined}
            barTopLabel={barTopLabel}
        />
    )
}

export default HouseVisitsStatusVisualization
